/**
 * AuthContext (DC-890)
 *
 * Provides authentication state and methods throughout the application.
 * Handles JWT token management, auto-refresh, and user session state.
 */

import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
// §5.8/§5.9 organization boundary (CC-V35-BANK-PROFILE-01): crossed on every
// token acquisition whose organization differs from the one currently held.
import { crossOrganizationBoundary } from '../hooks/organizationBoundary';

// ============================================================================
// TYPES
// ============================================================================

export type UserRole = 'admin' | 'analyst' | 'viewer';

export interface AuthUser {
  id: string;
  email: string;
  name: string;
  role: UserRole;
  organization: {
    id: string;
    name: string;
    slug: string;
  };
}

export interface AuthState {
  user: AuthUser | null;
  accessToken: string | null;
  isAuthenticated: boolean;
  isLoading: boolean;
}

export interface LoginCredentials {
  email: string;
  password: string;
}

export interface RegisterData {
  organizationName: string;
  fullName: string;
  email: string;
  password: string;
}

export interface InvitationDetails {
  email: string;
  fullName: string;
  role: UserRole;
  organizationName: string;
  expiresAt: string;
}

export interface MFARequired {
  mfaRequired: true;
  mfaToken: string;
  email: string;
}

export interface LoginResult {
  success: boolean;
  error?: string;
  mfa?: MFARequired;
}

export interface AuthContextValue extends AuthState {
  login: (credentials: LoginCredentials) => Promise<LoginResult>;
  completeMFALogin: (accessToken: string, user: AuthUser) => void;
  logout: () => Promise<void>;
  register: (data: RegisterData) => Promise<{ success: boolean; error?: string; userId?: string }>;
  refreshToken: () => Promise<boolean>;
  forgotPassword: (email: string) => Promise<{ success: boolean; error?: string }>;
  resetPassword: (token: string, password: string) => Promise<{ success: boolean; error?: string }>;
  verifyEmail: (token: string) => Promise<{ success: boolean; error?: string }>;
  getInvitationDetails: (token: string) => Promise<{ success: boolean; data?: InvitationDetails; error?: string }>;
  acceptInvitation: (token: string, password: string) => Promise<{ success: boolean; error?: string }>;
  hasPermission: (permission: string) => boolean;
  hasRole: (role: UserRole | UserRole[]) => boolean;
}

// ============================================================================
// CONTEXT
// ============================================================================

const AuthContext = createContext<AuthContextValue | undefined>(undefined);

// ============================================================================
// API HELPERS
// ============================================================================

const API_BASE = '/api/auth';

interface ApiError {
  code: string;
  message: string;
  details?: Record<string, unknown>;
}

interface ApiResponse<T> {
  data?: T;
  error?: ApiError;
}

async function apiRequest<T>(
  endpoint: string,
  options: RequestInit = {}
): Promise<ApiResponse<T>> {
  try {
    const response = await fetch(`${API_BASE}${endpoint}`, {
      ...options,
      headers: {
        'Content-Type': 'application/json',
        ...options.headers,
      },
      credentials: 'include', // Include cookies for refresh token
    });

    const json = await response.json();

    if (!response.ok) {
      return { error: json.error || { code: 'UNKNOWN_ERROR', message: 'Request failed' } };
    }

    return { data: json.data };
  } catch (error) {
    console.error('[AuthContext] API request failed:', error);
    return { error: { code: 'NETWORK_ERROR', message: 'Network request failed' } };
  }
}

// ============================================================================
// TOKEN STORAGE
// ============================================================================

const TOKEN_KEY = 'compass_access_token';
const USER_KEY = 'compass_user';

/**
 * THE single session-persistence contract (D-099 COR-2). Every token
 * acquisition — login, refresh, MFA completion, URL-token bootstrap — MUST
 * persist through this function and nowhere else; it crosses the §5.8
 * organization boundary BEFORE the new session snapshot is written, so a
 * switch clears the previous organization's cache/storage first. Same-org
 * refreshes are not a boundary. Exported so the acquisition-boundary suite
 * can pin the contract directly.
 */
export function saveAuthToStorage(token: string, user: AuthUser): void {
  if (user.organization?.id) {
    crossOrganizationBoundary(user.organization.id);
  }
  localStorage.setItem(TOKEN_KEY, token);
  localStorage.setItem(USER_KEY, JSON.stringify(user));
}

function clearAuthFromStorage(): void {
  localStorage.removeItem(TOKEN_KEY);
  localStorage.removeItem(USER_KEY);
}

function loadAuthFromStorage(): { token: string | null; user: AuthUser | null } {
  const token = localStorage.getItem(TOKEN_KEY);
  const userJson = localStorage.getItem(USER_KEY);

  let user: AuthUser | null = null;
  if (userJson) {
    try {
      user = JSON.parse(userJson);
    } catch {
      user = null;
    }
  }

  return { token, user };
}

// ============================================================================
// JWT HELPERS
// ============================================================================

interface JwtPayload {
  sub: string;
  org: string;
  email: string;
  name: string;
  role: UserRole;
  exp: number;
  iat: number;
}

function decodeJwt(token: string): JwtPayload | null {
  try {
    const parts = token.split('.');
    if (parts.length !== 3) return null;

    const payload = JSON.parse(atob(parts[1]));
    return payload;
  } catch {
    return null;
  }
}

/**
 * Bootstrap auth token from URL query param (dev-mode cross-origin support).
 *
 * In dev mode, legacy pages (port 3001) open the SPA (port 5173) with ?token=...
 * because localStorage is per-origin. This function picks up the token, decodes
 * the JWT to extract user info, saves both to localStorage, then cleans the URL.
 */
export function bootstrapTokenFromUrl(): void {
  const params = new URLSearchParams(window.location.search);
  const urlToken = params.get('token');
  if (urlToken && !localStorage.getItem(TOKEN_KEY)) {
    // Decode JWT to extract user info — initAuth() requires both token AND user
    const payload = decodeJwt(urlToken);
    if (payload) {
      const user: AuthUser = {
        id: payload.sub,
        email: payload.email,
        name: payload.name,
        role: payload.role,
        organization: { id: payload.org, name: '', slug: '' },
      };
      // D-099 COR-2: the URL-token path persists through the SAME contract
      // as every other acquisition — never by writing storage directly.
      saveAuthToStorage(urlToken, user);
    } else {
      // Undecodable token carries no organization; store it for initAuth's
      // expiry handling — with no user it cannot authenticate a session.
      localStorage.setItem(TOKEN_KEY, urlToken);
    }
    // Clean URL to avoid token leaking in browser history
    const cleanUrl = window.location.pathname + window.location.hash;
    window.history.replaceState(null, '', cleanUrl);
  }
}

// Run immediately on module load so token + user are available before React renders
bootstrapTokenFromUrl();

function isTokenExpired(token: string): boolean {
  const payload = decodeJwt(token);
  if (!payload) return true;

  // Consider expired if less than 5 minutes remaining
  const expiresAt = payload.exp * 1000;
  const bufferMs = 5 * 60 * 1000; // 5 minutes
  return Date.now() >= expiresAt - bufferMs;
}

function getTokenExpiresIn(token: string): number {
  const payload = decodeJwt(token);
  if (!payload) return 0;

  return Math.max(0, payload.exp * 1000 - Date.now());
}

// ============================================================================
// PROVIDER
// ============================================================================

interface AuthProviderProps {
  children: React.ReactNode;
}

export function AuthProvider({ children }: AuthProviderProps) {
  const [state, setState] = useState<AuthState>({
    user: null,
    accessToken: null,
    isAuthenticated: false,
    isLoading: true,
  });

  const refreshTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Reference to refreshToken for use in timeout callbacks
  const refreshTokenRef = useRef<(() => Promise<boolean>) | undefined>(undefined);

  // Schedule token refresh before expiry
  const scheduleTokenRefresh = useCallback((token: string) => {
    if (refreshTimeoutRef.current) {
      clearTimeout(refreshTimeoutRef.current);
    }

    const expiresIn = getTokenExpiresIn(token);
    // Refresh 5 minutes before expiry
    const refreshIn = Math.max(0, expiresIn - 5 * 60 * 1000);

    if (refreshIn > 0) {
      refreshTimeoutRef.current = setTimeout(() => {
        refreshTokenRef.current?.();
      }, refreshIn);
    }
  }, []);

  // Refresh access token using refresh token cookie
  const refreshToken = useCallback(async (): Promise<boolean> => {
    const response = await apiRequest<{ accessToken: string; expiresIn: number }>('/refresh', {
      method: 'POST',
    });

    if (response.data) {
      const { accessToken } = response.data;
      const payload = decodeJwt(accessToken);

      if (payload) {
        const user: AuthUser = {
          id: payload.sub,
          email: payload.email,
          name: payload.name,
          role: payload.role,
          organization: {
            id: payload.org,
            name: state.user?.organization.name || '',
            slug: state.user?.organization.slug || '',
          },
        };

        saveAuthToStorage(accessToken, user);
        setState(prev => ({
          ...prev,
          accessToken,
          user: { ...user, organization: prev.user?.organization || user.organization },
          isAuthenticated: true,
          isLoading: false,
        }));
        scheduleTokenRefresh(accessToken);
        return true;
      }
    }

    // Refresh failed - clear auth state
    clearAuthFromStorage();
    setState({
      user: null,
      accessToken: null,
      isAuthenticated: false,
      isLoading: false,
    });
    return false;
  }, [scheduleTokenRefresh, state.user?.organization]);

  // Keep ref updated with latest refreshToken
  useEffect(() => {
    refreshTokenRef.current = refreshToken;
  }, [refreshToken]);

  // Initialize auth state from storage
  useEffect(() => {
    const initAuth = async () => {
      const { token, user } = loadAuthFromStorage();

      if (token && user) {
        // Establish (not cross) the organization boundary for the restored
        // session — booting into an organization is not a switch, so this
        // clears nothing; it arms the boundary for a later real switch.
        if (user.organization?.id) {
          crossOrganizationBoundary(user.organization.id);
        }
        if (isTokenExpired(token)) {
          // Try to refresh
          const refreshed = await refreshToken();
          if (!refreshed) {
            setState({
              user: null,
              accessToken: null,
              isAuthenticated: false,
              isLoading: false,
            });
          }
        } else {
          // Token is valid
          setState({
            user,
            accessToken: token,
            isAuthenticated: true,
            isLoading: false,
          });
          scheduleTokenRefresh(token);
        }
      } else {
        setState(prev => ({ ...prev, isLoading: false }));
      }
    };

    initAuth();

    // Cleanup on unmount
    return () => {
      if (refreshTimeoutRef.current) {
        clearTimeout(refreshTimeoutRef.current);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- Only run once on mount
  }, []);

  // Login
  const login = useCallback(
    async (credentials: LoginCredentials): Promise<LoginResult> => {
      setState(prev => ({ ...prev, isLoading: true }));

      const response = await apiRequest<{
        accessToken?: string;
        expiresIn?: number;
        user?: AuthUser;
        mfaRequired?: boolean;
        mfaToken?: string;
      }>('/login', {
        method: 'POST',
        body: JSON.stringify(credentials),
      });

      if (response.error) {
        setState(prev => ({ ...prev, isLoading: false }));
        return { success: false, error: response.error.message };
      }

      if (response.data) {
        // Check if MFA is required
        if (response.data.mfaRequired && response.data.mfaToken) {
          setState(prev => ({ ...prev, isLoading: false }));
          return {
            success: false,
            mfa: {
              mfaRequired: true,
              mfaToken: response.data.mfaToken!,
              email: credentials.email,
            },
          };
        }

        // Normal login (no MFA or MFA not enabled)
        if (response.data.accessToken && response.data.user) {
          const { accessToken, user } = response.data;
          saveAuthToStorage(accessToken, user);
          setState({
            user,
            accessToken,
            isAuthenticated: true,
            isLoading: false,
          });
          scheduleTokenRefresh(accessToken);
          return { success: true };
        }
      }

      setState(prev => ({ ...prev, isLoading: false }));
      return { success: false, error: 'Unknown error' };
    },
    [scheduleTokenRefresh]
  );

  // Complete MFA login after successful verification
  const completeMFALogin = useCallback(
    (accessToken: string, user: AuthUser) => {
      saveAuthToStorage(accessToken, user);
      setState({
        user,
        accessToken,
        isAuthenticated: true,
        isLoading: false,
      });
      scheduleTokenRefresh(accessToken);
    },
    [scheduleTokenRefresh]
  );

  // Logout
  const logout = useCallback(async (): Promise<void> => {
    if (refreshTimeoutRef.current) {
      clearTimeout(refreshTimeoutRef.current);
    }

    await apiRequest('/logout', {
      method: 'POST',
      headers: state.accessToken
        ? { Authorization: `Bearer ${state.accessToken}` }
        : undefined,
    });

    clearAuthFromStorage();
    setState({
      user: null,
      accessToken: null,
      isAuthenticated: false,
      isLoading: false,
    });
  }, [state.accessToken]);

  // Register
  const register = useCallback(
    async (
      data: RegisterData
    ): Promise<{ success: boolean; error?: string; userId?: string }> => {
      setState(prev => ({ ...prev, isLoading: true }));

      const response = await apiRequest<{
        message: string;
        organizationId: string;
        userId: string;
      }>('/register', {
        method: 'POST',
        body: JSON.stringify(data),
      });

      setState(prev => ({ ...prev, isLoading: false }));

      if (response.error) {
        return { success: false, error: response.error.message };
      }

      if (response.data) {
        return { success: true, userId: response.data.userId };
      }

      return { success: false, error: 'Unknown error' };
    },
    []
  );

  // Forgot Password
  const forgotPassword = useCallback(
    async (email: string): Promise<{ success: boolean; error?: string }> => {
      const response = await apiRequest<{ message: string }>('/forgot-password', {
        method: 'POST',
        body: JSON.stringify({ email }),
      });

      if (response.error) {
        return { success: false, error: response.error.message };
      }

      return { success: true };
    },
    []
  );

  // Reset Password
  const resetPassword = useCallback(
    async (token: string, password: string): Promise<{ success: boolean; error?: string }> => {
      const response = await apiRequest<{ message: string }>(`/reset-password/${token}`, {
        method: 'POST',
        body: JSON.stringify({ password }),
      });

      if (response.error) {
        return { success: false, error: response.error.message };
      }

      return { success: true };
    },
    []
  );

  // Verify Email
  const verifyEmail = useCallback(
    async (token: string): Promise<{ success: boolean; error?: string }> => {
      const response = await apiRequest<{ message: string }>(`/verify-email/${token}`, {
        method: 'GET',
      });

      if (response.error) {
        return { success: false, error: response.error.message };
      }

      return { success: true };
    },
    []
  );

  // Get Invitation Details
  const getInvitationDetails = useCallback(
    async (token: string): Promise<{ success: boolean; data?: InvitationDetails; error?: string }> => {
      const response = await apiRequest<InvitationDetails>(`/invitation/${token}`, {
        method: 'GET',
      });

      if (response.error) {
        return { success: false, error: response.error.message };
      }

      return { success: true, data: response.data };
    },
    []
  );

  // Accept Invitation
  const acceptInvitation = useCallback(
    async (token: string, password: string): Promise<{ success: boolean; error?: string }> => {
      const response = await apiRequest<{ message: string; userId: string }>(
        `/accept-invitation/${token}`,
        {
          method: 'POST',
          body: JSON.stringify({ password }),
        }
      );

      if (response.error) {
        return { success: false, error: response.error.message };
      }

      return { success: true };
    },
    []
  );

  // Permission check
  const hasPermission = useCallback(
    (permission: string): boolean => {
      if (!state.user) return false;

      // Admin has all permissions
      if (state.user.role === 'admin') return true;

      // Define role permissions
      const rolePermissions: Record<UserRole, string[]> = {
        admin: ['*'],
        analyst: [
          'journey:create',
          'journey:read',
          'journey:update',
          'journey:delete',
          'dataset:create',
          'dataset:read',
          'dataset:update',
          'dataset:delete',
          'analysis:create',
          'analysis:read',
          'analysis:update',
          'analysis:delete',
          'user:read',
        ],
        viewer: ['journey:read', 'dataset:read', 'analysis:read', 'user:read'],
      };

      const permissions = rolePermissions[state.user.role];
      return permissions.includes('*') || permissions.includes(permission);
    },
    [state.user]
  );

  // Role check
  const hasRole = useCallback(
    (role: UserRole | UserRole[]): boolean => {
      if (!state.user) return false;

      const roles = Array.isArray(role) ? role : [role];
      return roles.includes(state.user.role);
    },
    [state.user]
  );

  const value: AuthContextValue = {
    ...state,
    login,
    completeMFALogin,
    logout,
    register,
    refreshToken,
    forgotPassword,
    resetPassword,
    verifyEmail,
    getInvitationDetails,
    acceptInvitation,
    hasPermission,
    hasRole,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

// ============================================================================
// HOOK
// ============================================================================

export function useAuth(): AuthContextValue {
  const context = useContext(AuthContext);

  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }

  return context;
}

export default AuthContext;
